/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { ChangeEvent } from 'react'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Select } from '@nexigendigital/nxui'
import { useUpdateCartItemsMutation } from 'api/shop'
import { updateBillingCycle } from 'store/slices/shopSlice'
import { ProductServiceType } from 'models/enums'
import { CartItem, PendingItem } from 'models/shop'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { aud, getBillingCycle } from 'helpers/utils'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    item: CartItem
    isDiscounted: boolean
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function BillingCyclePricing({ item, isDiscounted }: Props) {
    const dispatch = useAppDispatch()
    const { items, baseConfigOptions, hosting, cart, cartUpdateStatus } = useAppSelector((state) => state.shop)
    const { product_id, name, billing_cycle_id } = item
    const pendingItem = items.find((pendingItem) => pendingItem.product_id === product_id && pendingItem.name === name)
    const [updateCartItem] = useUpdateCartItemsMutation()

    function synchroniseWithCart(event: ChangeEvent<{ value: string }>) {
        if (cart) {
            const updateItems = cart.items.map((cartItem) => {
                if (cartItem.product_id === item.product_id && cartItem.name === item.name) {
                    return {
                        ...cartItem,
                        billing_cycle_id: parseInt(event.target.value, 10)
                    }
                }

                return cartItem
            })

            updateCartItem({ uuid: cart.uuid, items: updateItems })
        }
    }

    if (pendingItem?.product_type === ProductServiceType.SharedHosting) {
        const { config_option_groups, pricing }: any = hosting
        const { config_options } = config_option_groups[0]
        const baseConfigs = baseConfigOptions.map((baseConfigOption) => baseConfigOption.name)

        const pricingBillingCycle = (pricingBillingCycleId: number) => {
            return config_options.map((config: { name: string }) => {
                if (baseConfigs.includes(config.name)) {
                    const { value }: any = baseConfigOptions.find((baseConfigOption) => baseConfigOption.name === config.name)
                    const { values }: any = config
                    const { pricing: optionPricing } = values.find(({ name: optionName }: { name: string }) => optionName === value)
                    const { price } = optionPricing.find(({ billing_cycle: { id } }: { billing_cycle: { id: number } }) => id === pricingBillingCycleId)

                    return parseFloat(price)
                }

                return 0
            }).reduce((prev: number, next: number) => prev + next, 0)
        }

        return (
            <Select
                value={billing_cycle_id}
                disabled={cartUpdateStatus === 'loading'}
                onChange={(event: ChangeEvent<{ value: string }>) => {
                    dispatch(updateBillingCycle({
                        product_id,
                        name,
                        billing_cycle_id: event.target.value,
                        type: 'custom-hosting'
                    }))
                    synchroniseWithCart(event)
                }}
            >
                {pricing.map(({ billing_cycle }: { billing_cycle: { id: number, name: string } }) => {
                    return (
                        <option key={billing_cycle.id} value={billing_cycle.id}>
                            {isDiscounted
                                ? `${item.price.discount} /${billing_cycle.name}`
                                : `${aud(pricingBillingCycle(billing_cycle.id))} /${billing_cycle.name}`
                            }
                        </option>
                    )
                })}
            </Select>
        )
    }

    if (pendingItem?.product_type === 'domain') {
        const { billing_cycle_id: currentBillingCycle }: any = items.find((pendingItem: PendingItem) => pendingItem.product_id === item.product_id && pendingItem.name === item.name)
        const pricing = pendingItem?.pricing_list.filter(({ type }) => type === item.order_type)

        return (
            <Select
                value={currentBillingCycle}
                disabled={cartUpdateStatus === 'loading'}
                onChange={(event: ChangeEvent<{ value: string }>) => {
                    const value = parseInt(event.target.value, 10)

                    dispatch(updateBillingCycle({
                        product_id: item.product_id,
                        name: item.name,
                        billing_cycle_id: value,
                        type: item.order_type
                    }))
                    synchroniseWithCart(event)
                }}
            >
                {pricing?.map((pricingList) => {
                    return (
                        <option key={pricingList.billing_cycle_id} value={pricingList.billing_cycle_id}>
                            {aud(pricingList.price)}
                            {' '}
                            {pricingList.billing_cycle
                                ? getBillingCycle(String(pricingList.billing_cycle.name))
                                : `/${pricingList.billing_cycle_id}`
                            }
                        </option>
                    )
                })}
            </Select>
        )
    }

    return null
}
